import { ColDef, RowClickedEvent, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	aggridHeaderClass
} from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {
	createSaveColumnState,
	defaultColDef,
	getDefaultRowIndexColumnConfig,
	getRowStyle,
	IndexParameterCell,
	onGridReady
} from "src/utils/grid/gridStyleUtils";
import { MST7020_R01Dto, defaultMST7020_R01Dto } from "./Dto_MST7020_R01";
import { senderStatusMap_MST7020_R01, sendTypeMap_MST7020_R01, useMST7020_R01Store } from "./Store_MST7020_R01";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';
	
const COLUMN_STATE_KEY = 'MST7020_R01-grid-column-state';

export interface Props {
	onRowClick?: (rowData: MST7020_R01Dto) => void;
	gridRef: React.RefObject<AgGridReact<MST7020_R01Dto> | null>;
	defaultMST7020_R01Row: MST7020_R01Dto;
}

export const Table_MST7020_R01 = (props: Props) => {

	const { onRowClick, gridRef, defaultMST7020_R01Row } = props;

	const { 
		MST7020_R01List, 
		editMST7020_R01, setEditMST7020_R01,
		selectedMST7020_R01, setSelectedMST7020_R01, initSelectedMST7020_R01,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = useMST7020_R01Store();
	
	//const gridRef = useRef<AgGridReact>(null);
	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'sendMsgId',
		gridData: MST7020_R01List,
		defaultGridData: defaultMST7020_R01Row,
		selectedRow: selectedMST7020_R01,
	});
	
	// 호버 상태 관리
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const headerNames: Record<string, string> = {
        rowIndex: "",
		sendMsgId: '전송번호',
    }

	const getCellStyle = useCallback((params: any, key: string) => onlyTouchGridStyle(key, 'sendMsgId')(params), []);

	const baseColumnDefs: ColDef<MST7020_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: "전송번호", colId: "sendMsgId", field: "sendMsgId", width: 80, 
			cellStyle: (params) => getCellStyle(params, 'sendMsgId'), 
			valueFormatter: (params: ValueFormatterParams) => {
				// 고정된 행(pinned row)의 경우, 문자열 값을 그대로 반환합니다.
				if (params.node?.rowPinned) {
					return params.value;
				}
				// 일반 데이터 행의 경우, 기존 숫자 값을 반환합니다.
				return params.value;
			}
		},
		{
			headerName: "전송타입", colId: "sendType", field: "sendType", width: 100, 
			cellStyle: (params) => getCellStyle(params, 'sendType'), 
			cellRenderer: (params: any) => {
				return sendTypeMap_MST7020_R01.find(item => item.value === params.value)?.title || params.value;
			},
		},
		{
			headerName: "전송상태", colId: "senderStatus", field: "senderStatus", width: 80, 
			cellStyle: (params) => getCellStyle(params, 'senderStatus'), 
			cellRenderer: (params: any) => {
				return senderStatusMap_MST7020_R01.find(item => item.value === params.value)?.title || params.value;
			},
		},
		{
			headerName: "예약일시", colId: "reserveDT", field: "reserveDT", width: 130, 
			cellStyle: (params) => getCellStyle(params, 'reserveDT'), 
		},
		{
			headerName: "발신자번호", colId: "sender", field: "sender", width: 100, 
			cellStyle: (params) => getCellStyle(params, 'sender'), 
		},
		{
			headerName: "발신자명", colId: "senderName", field: "senderName", width: 100, 
			cellStyle: (params) => getCellStyle(params, 'senderName'), 
		},
		{
			headerName: "수신자번호", colId: "receiver", field: "receiver", width: 100, 
			cellStyle: (params) => getCellStyle(params, 'receiver'), 
		},
		{
			headerName: "수신자명", colId: "receiverName", field: "receiverName", width: 100, 
			cellStyle: (params) => getCellStyle(params, 'receiverName'), 
		},
		{
			headerName: "제목", colId: "subject", field: "subject", width: 150, 
			cellStyle: (params) => getCellStyle(params, 'subject'), 
		},
	], [hoveredRowIndex, MST7020_R01List, selectedMST7020_R01]);
	


	const columnDefs: ColDef<MST7020_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<MST7020_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, columnState, headerNames]);

	

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		sendMsgId: MST7020_R01List.length.toLocaleString() + "건",
		editable: false,
	}]), [MST7020_R01List]);

	
	const handleRowClick = useCallback((e: RowClickedEvent<MST7020_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 계좌와 같은지 확인
		const isSelected = selectedMST7020_R01?.sendMsgId === rowData.sendMsgId;
		if (isSelected) {
			setSelectedMST7020_R01(undefined);
		} else {
			setSelectedMST7020_R01(rowData);
		}
	}, [selectedMST7020_R01, setSelectedMST7020_R01]);

	useEffect(() => { ensureSelectedRow(); }, [MST7020_R01List, selectedMST7020_R01]);

	return (
		<GridContextWrapper<MST7020_R01Dto>
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			//setPaddedGridData={setPaddedGridData}
			initSelected={initSelectedMST7020_R01}
			defaultRowData={defaultMST7020_R01Row}
			baseColumnDefs={columnDefs}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<MST7020_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}			defaultColDef={defaultColDef}
				getRowId={getRowId}
				
				rowClassRules={createRowClassRules<MST7020_R01Dto>({
					idKey: 'sendMsgId',
					selectedList: selectedMST7020_R01 ? [selectedMST7020_R01.sendMsgId] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}    

				// Grid_Select에 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 호버 상태 이벤트를 위한 저장
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setColumnState}				//컬럼 상태 저장
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};

export default Table_MST7020_R01;